import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Document } from "@/types/models";
import { useDeleteDocument } from "@/hooks/useDocumentMetadata";
import { useIndexingQueue } from "@/hooks/useIndexingQueue";
import { useDeleteProjectDocuments } from "@/hooks/useProjects";
import { projectsApi } from "@/services/projectsApi";

export type DeleteStage = "idle" | "unlinking" | "deindexing" | "deleting";

/**
 * Permanently deletes a document (file, metadata, and index entries) with a confirmation
 * step. When projectId is given, also unlinks it from that project and cleans up any
 * stale template source references first.
 */
export function useDeleteEvidenceDocument(projectId?: string) {
  const [deletingDoc, setDeletingDoc] = useState<Document | null>(null);
  const [deleteStage, setDeleteStage] = useState<DeleteStage>("idle");

  const qc = useQueryClient();
  const deleteDocument = useDeleteDocument();
  const indexingQueue = useIndexingQueue();
  const deleteProjectDocuments = useDeleteProjectDocuments();

  const processAndDelete = useCallback(async () => {
    if (!deletingDoc) return;
    // Set here too (idempotent) so the no-projectId branch of handleDelete,
    // which calls processAndDelete() directly, is covered as well.
    setDeleteStage("deindexing");
    try {
      await indexingQueue.enqueue(
        [{ document_id: deletingDoc.id, filename: deletingDoc.filename, doc_action: "remove" }],
        projectId ?? undefined,
      );
      indexingQueue.startPolling([deletingDoc.id], () => {
        setDeleteStage("deleting");
        deleteDocument.mutate(deletingDoc.id, {
          onSuccess: () => {
            setDeleteStage("idle");
            setDeletingDoc(null);
            indexingQueue.reset();
          },
          onError: () => setDeleteStage("idle"),
        });
      });
    } catch {
      // enqueue error is surfaced via indexingQueue.documentStatuses
      setDeleteStage("idle");
    }
  }, [deletingDoc, projectId, indexingQueue, deleteDocument]);

  const handleDelete = useCallback(() => {
    if (!deletingDoc) return;
    setDeleteStage("unlinking");
    if (projectId) {
      deleteProjectDocuments.mutate(
        { projectId, documentIds: [deletingDoc.id] },
        {
          onSuccess: async () => {
            // Read all cached template queries for this project (covers Reporting, Outline, etc.)
            // so we can find every section that references the deleted document.
            const cachedTemplates = qc.getQueriesData<{
              prompt_libraries: { id: string; sources: { document_ids: string[]; extraction_point_ids: string[] } | null }[];
            }>({ queryKey: ["projects", projectId, "template"] });

            try {
              // For each section whose source list includes the deleted document,
              // patch the backend to remove the stale ID.
              await Promise.all(
                cachedTemplates.flatMap(([, data]) =>
                  (data?.prompt_libraries ?? [])
                    .filter((pl) => pl.sources?.document_ids?.includes(deletingDoc.id))
                    .map((pl) =>
                      projectsApi.updateProjectPromptLibrary(projectId, pl.id, {
                        sources: {
                          document_ids: pl.sources!.document_ids.filter((id) => id !== deletingDoc.id),
                          extraction_point_ids: pl.sources!.extraction_point_ids ?? [],
                        },
                      })
                    )
                )
              );
            } catch (err) {
              // Best-effort cleanup of stale template references — don't block
              // the delete itself on this (matches the sibling onError below).
              console.error("Failed to clean up template document references:", err);
            } finally {
              // Invalidate cached template data so the UI re-fetches
              // and reflects any cleaned source lists immediately.
              qc.invalidateQueries({ queryKey: ["projects", projectId, "template"] });
              setDeleteStage("deindexing");
              processAndDelete();
            }
          },
          onError: processAndDelete,
        }
      );
    } else {
      processAndDelete();
    }
  }, [deletingDoc, projectId, deleteProjectDocuments, processAndDelete, qc]);

  const errorMessage =
    deletingDoc && indexingQueue.documentStatuses[deletingDoc.id]?.status === "Error"
      ? `Failed to remove file: ${indexingQueue.documentStatuses[deletingDoc.id]?.error ?? "Unknown error"}`
      : deleteDocument.isError
        ? `Failed to delete metadata: ${deleteDocument.error?.message}`
        : null;

  return { deletingDoc, setDeletingDoc, deleteStage, handleDelete, errorMessage };
}
